import { useState } from "react";
import { Plus, Loader2, Check, X } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { formatApiErrorDetail } from "@/lib/api";

const ADD_NEW = "__add_new__";

/** A normal Select, plus an inline "+ Add new" entry at the bottom of its list.
 * Picking it swaps the trigger for a small input right in the same form -
 * saving creates the option and selects it immediately, no navigation away
 * to Settings. Used for the Category/Form/Unit dropdowns in the item form. */
export default function SelectWithInlineAdd({ value, onChange, options, onCreate, placeholder, addLabel = "Add new", testId }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleValueChange = (v) => {
    if (v === ADD_NEW) {
      setAdding(true);
      return;
    }
    onChange(v);
  };

  const handleSave = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const newOption = await onCreate(newName.trim());
      onChange(newOption.id);
      setAdding(false);
      setNewName("");
    } catch (err) {
      toast({ variant: "destructive", title: "Could not add option", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  if (adding) {
    return (
      <div className="flex gap-2" data-testid={`${testId}-inline-add`}>
        <Input
          autoFocus
          placeholder={addLabel}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
          data-testid={`${testId}-inline-add-input`}
        />
        <Button type="button" size="icon" onClick={handleSave} disabled={saving || !newName.trim()} data-testid={`${testId}-inline-add-save`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        </Button>
        <Button type="button" size="icon" variant="outline" onClick={() => { setAdding(false); setNewName(""); }} data-testid={`${testId}-inline-add-cancel`}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <Select value={value} onValueChange={handleValueChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}{o.active === false ? " (inactive)" : ""}</SelectItem>)}
        <SelectItem value={ADD_NEW} data-testid={`${testId}-add-new-option`}>
          <span className="flex items-center gap-1 text-amtm-teal font-medium"><Plus className="h-3.5 w-3.5" /> {addLabel}</span>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
